import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getCostCenterTypes, getAllFinancialYears, getCCS,
    getConsolidateCashflowReportGrid, getConsolidateCashflowReportDetail,
    getConsolidateInvoiceReceiptDetailViewReportGrid,
} from '../../api/ReportsAPI/consolidateCashflowReportAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

export const fetchCCTypes = createAsyncThunk('consolidateCashflowReport/fetchCCTypes', async (_, { rejectWithValue }) => {
    try { const res = await getCostCenterTypes(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch cost centre types')); }
});

export const fetchFinancialYears = createAsyncThunk('consolidateCashflowReport/fetchFinancialYears', async (_, { rejectWithValue }) => {
    try { const res = await getAllFinancialYears(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch financial years')); }
});

export const fetchCCList = createAsyncThunk('consolidateCashflowReport/fetchCCList', async (params, { rejectWithValue }) => {
    try { const res = await getCCS(params); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch cost centres')); }
});

export const fetchConsolidateCashflowReport = createAsyncThunk('consolidateCashflowReport/fetchReport', async (params, { rejectWithValue }) => {
    try { const res = await getConsolidateCashflowReportGrid(params); return res?.Data || null; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch consolidate cashflow report')); }
});

export const fetchCashflowDetail = createAsyncThunk('consolidateCashflowReport/fetchDetail', async ({ rowKey, ...params }, { rejectWithValue }) => {
    try { const res = await getConsolidateCashflowReportDetail(params); return { rowKey, data: res?.Data || [] }; }
    catch (e) { return rejectWithValue({ rowKey, message: err(e, 'Failed to fetch detail') }); }
});

export const fetchInvoiceReceiptDetail = createAsyncThunk('consolidateCashflowReport/fetchInvoiceReceiptDetail', async (params, { rejectWithValue }) => {
    try { const res = await getConsolidateInvoiceReceiptDetailViewReportGrid(params); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch invoice/receipt detail')); }
});

const initialState = {
    ccTypes: [], ccTypesLoading: false,
    years: [], yearsLoading: false,
    ccList: [], ccListLoading: false,

    report: null, reportLoading: false, reportError: null,

    detailByRow: {}, detailLoadingByRow: {},

    invoiceDetail: [], invoiceDetailLoading: false, invoiceDetailError: null,
};

const consolidateCashflowReportSlice = createSlice({
    name: 'consolidateCashflowReport',
    initialState,
    reducers: {
        clearReport: (s) => { s.report = null; s.reportError = null; s.detailByRow = {}; s.detailLoadingByRow = {}; },
        clearInvoiceDetail: (s) => { s.invoiceDetail = []; s.invoiceDetailError = null; },
        resetConsolidateCashflowReport: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCCTypes.pending, (s) => { s.ccTypesLoading = true; })
            .addCase(fetchCCTypes.fulfilled, (s, a) => { s.ccTypesLoading = false; s.ccTypes = a.payload; })
            .addCase(fetchCCTypes.rejected, (s) => { s.ccTypesLoading = false; });

        builder
            .addCase(fetchFinancialYears.pending, (s) => { s.yearsLoading = true; })
            .addCase(fetchFinancialYears.fulfilled, (s, a) => { s.yearsLoading = false; s.years = a.payload; })
            .addCase(fetchFinancialYears.rejected, (s) => { s.yearsLoading = false; });

        builder
            .addCase(fetchCCList.pending, (s) => { s.ccListLoading = true; })
            .addCase(fetchCCList.fulfilled, (s, a) => { s.ccListLoading = false; s.ccList = a.payload; })
            .addCase(fetchCCList.rejected, (s) => { s.ccListLoading = false; });

        builder
            .addCase(fetchConsolidateCashflowReport.pending, (s) => { s.reportLoading = true; s.reportError = null; })
            .addCase(fetchConsolidateCashflowReport.fulfilled, (s, a) => { s.reportLoading = false; s.report = a.payload; s.detailByRow = {}; s.detailLoadingByRow = {}; })
            .addCase(fetchConsolidateCashflowReport.rejected, (s, a) => { s.reportLoading = false; s.reportError = a.payload; });

        builder
            .addCase(fetchCashflowDetail.pending, (s, a) => { s.detailLoadingByRow[a.meta.arg.rowKey] = true; })
            .addCase(fetchCashflowDetail.fulfilled, (s, a) => { s.detailLoadingByRow[a.payload.rowKey] = false; s.detailByRow[a.payload.rowKey] = a.payload.data; })
            .addCase(fetchCashflowDetail.rejected, (s, a) => { s.detailLoadingByRow[a.meta.arg.rowKey] = false; });

        builder
            .addCase(fetchInvoiceReceiptDetail.pending, (s) => { s.invoiceDetailLoading = true; s.invoiceDetailError = null; })
            .addCase(fetchInvoiceReceiptDetail.fulfilled, (s, a) => { s.invoiceDetailLoading = false; s.invoiceDetail = a.payload; })
            .addCase(fetchInvoiceReceiptDetail.rejected, (s, a) => { s.invoiceDetailLoading = false; s.invoiceDetailError = a.payload; });
    },
});

export const { clearReport, clearInvoiceDetail, resetConsolidateCashflowReport } = consolidateCashflowReportSlice.actions;

export const selectConsolidateCashflowReport = (s) => s.consolidateCashflowReport;

export default consolidateCashflowReportSlice.reducer;
